import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart3, Trash2, Calendar, Star, Download, Database, Inbox } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { UserLayout } from '../components/User/UserLayout';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface SurveyResponse {
  id: string;
  response_data: Record<string, any>;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

interface PageInfo {
  id: string;
  name: string;
}

interface SurveyQuestion {
  id: string;
  type: string;
  question: string;
  required: boolean;
  options?: string[];
  maxRating?: number;
}

interface SurveyStats {
  [questionId: string]: {
    question: string;
    type: string;
    totalResponses: number;
    distribution?: Record<string, number>;
    percentages?: Record<string, string>;
    average?: string;
    min?: number;
    max?: number;
    responses?: string[];
  };
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export const SurveyResults: React.FC = () => {
  const { pageId, componentId } = useParams<{ pageId: string; componentId: string }>();
  const { token } = useAuthStore();
  
  const [page, setPage] = useState<PageInfo | null>(null);
  const [survey, setSurvey] = useState<any>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [stats, setStats] = useState<SurveyStats>({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);

  useEffect(() => {
    loadSurveyData();
  }, [pageId, componentId]);

  const loadSurveyData = async () => {
    try {
      setIsLoading(true);
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      
      const response = await fetch(`${API_URL}/surveys/${pageId}/${componentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load survey data');
      }

      const data = await response.json();
      setPage(data.page);
      setSurvey(data.survey);
      setResponses(data.responses);
      setStats(data.stats);
    } catch (error) {
      console.error('Load survey data error:', error);
      toast.error('Failed to load survey data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (responseId: string) => {
    if (!confirm('Are you sure you want to delete this response?')) {
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      
      const response = await fetch(`${API_URL}/surveys/response/${responseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete response');
      }

      toast.success('Response deleted');
      loadSurveyData();
      setSelectedResponse(null);
    } catch (error) {
      console.error('Delete response error:', error);
      toast.error('Failed to delete response');
    }
  };

  const handleExport = () => {
    if (responses.length === 0) {
      toast.error('No responses to export');
      return;
    }

    // Get all questions from survey (support both sections and legacy questions)
    let allQuestions: SurveyQuestion[] = [];
    
    if (survey.settings.enableSections && survey.settings.sections) {
      // Multi-page survey: flatten all questions from all sections
      allQuestions = survey.settings.sections.flatMap((section: any) => section.questions || []);
    } else if (survey.settings.questions) {
      // Legacy single-page survey
      allQuestions = survey.settings.questions;
    }

    if (allQuestions.length === 0) {
      toast.error('No questions found in survey');
      return;
    }

    // Build CSV headers (only include questions that were set in the survey)
    const headers = ['Date', 'Time', ...allQuestions.map((q: SurveyQuestion) => q.question), 'IP Address'];
    
    // Build CSV rows
    const rows = responses.map(r => {
      const date = new Date(r.created_at);
      const row = [
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        ...allQuestions.map((q: SurveyQuestion) => {
          const answer = r.response_data[q.id];
          if (Array.isArray(answer)) {
            // For checkbox questions (multiple selections)
            return answer.join(', ');
          }
          // Return actual answer or empty string (not "N/A")
          return answer || '';
        }),
        r.ip_address || '',
      ];
      return row.map(v => `"${v}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey-results-${page?.name}-${new Date().toISOString()}.csv`;
    a.click();
    toast.success('Exported to CSV!');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const latestResponse = responses[0];
  const last24h = responses.filter(r => {
    const diff = Date.now() - new Date(r.created_at).getTime();
    return diff < 24 * 60 * 60 * 1000;
  }).length;

  if (isLoading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading survey results...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">📊 Survey Results</h1>
            <p className="text-gray-600 mt-1">
              Page: <span className="font-semibold">{page?.name}</span>
              {survey && <span className="text-gray-400 ml-2">• {survey.settings.title}</span>}
            </p>
          </div>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
            disabled={responses.length === 0}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Responses</p>
              <Database className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{responses.length}</p>
            <p className="text-xs text-gray-400 mt-2">All time</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Last 24 Hours</p>
              <Inbox className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{last24h}</p>
            <p className="text-xs text-gray-400 mt-2">Recent responses</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Latest Response</p>
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-lg font-bold text-gray-900">
              {latestResponse ? formatDate(latestResponse.created_at).split(',')[0] : 'N/A'}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {latestResponse ? formatDate(latestResponse.created_at).split(',')[1] : ''}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Questions</p>
              <BarChart3 className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {survey?.settings.questions.length || 0}
            </p>
            <p className="text-xs text-gray-400 mt-2">Survey questions</p>
          </div>
        </div>

        {/* Question Statistics */}
        {survey && survey.settings.questions.map((question: SurveyQuestion, index: number) => {
          const questionStats = stats[question.id];
          if (!questionStats) return null;

          return (
            <div key={question.id} className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{question.question}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {questionStats.totalResponses} responses • {question.type}
                  </p>
                </div>
              </div>

              {/* Multiple Choice / Yes-No - Pie Chart */}
              {(question.type === 'multipleChoice' || question.type === 'yesNo') && questionStats.distribution && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={Object.entries(questionStats.distribution).map(([key, value]) => ({
                            name: key,
                            value: value,
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.keys(questionStats.distribution).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <div className="space-y-3">
                      {Object.entries(questionStats.distribution).map(([option, count], idx) => (
                        <div key={option} className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">{option}</span>
                              <span className="text-sm text-gray-600">
                                {count} ({questionStats.percentages?.[option]}%)
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${questionStats.percentages?.[option]}%`,
                                  backgroundColor: COLORS[idx % COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Checkbox - Bar Chart */}
              {question.type === 'checkbox' && questionStats.distribution && (
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={Object.entries(questionStats.distribution).map(([key, value]) => ({
                        name: key,
                        count: value,
                        percentage: questionStats.percentages?.[key] || 0,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Note: Percentages may exceed 100% (multiple selections allowed)
                  </p>
                </div>
              )}

              {/* Rating - Stars */}
              {question.type === 'rating' && (
                <div className="flex items-center gap-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                      <span className="text-4xl font-bold text-gray-900">{questionStats.average}</span>
                      <span className="text-gray-500">/ {question.maxRating}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Average rating from {questionStats.totalResponses} responses
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Min: {questionStats.min} • Max: {questionStats.max}
                    </p>
                  </div>
                  {questionStats.distribution && (
                    <div className="flex-1">
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart
                          data={Object.entries(questionStats.distribution)
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([rating, count]) => ({
                              rating: `${rating}⭐`,
                              count: count,
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="rating" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#fbbf24" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}

              {/* Text / Textarea - List */}
              {(question.type === 'text' || question.type === 'textarea') && questionStats.responses && (
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                  {questionStats.responses.map((response, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 whitespace-pre-wrap">{response}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* All Responses Section */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">All Responses ({responses.length})</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Responses List */}
            <div className="space-y-4">
              {responses.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                  <Inbox size={64} className="text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No responses yet</h3>
                  <p className="text-gray-600">Responses will appear here when users complete the survey.</p>
                </div>
              ) : (
                responses.map((response) => (
                  <div
                    key={response.id}
                    onClick={() => setSelectedResponse(response)}
                    className={`bg-white border ${
                      selectedResponse?.id === response.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                    } rounded-xl p-4 cursor-pointer transition-all hover:shadow-md hover:border-blue-300`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={14} />
                        {formatDate(response.created_at)}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(response.id);
                        }}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-900">
                      {Object.keys(response.response_data).length} answers
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Response Detail */}
            <div className="lg:sticky lg:top-8 h-fit">
              {selectedResponse && survey ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Response Details</h3>
                    <button
                      onClick={() => handleDelete(selectedResponse.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>

                  <div className="space-y-4">
                    {survey.settings.questions.map((question: SurveyQuestion, idx: number) => {
                      const answer = selectedResponse.response_data[question.id];
                      
                      return (
                        <div key={question.id} className="border-b border-gray-100 pb-4">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <label className="text-sm font-semibold text-gray-700">{question.question}</label>
                          </div>
                          <div className="ml-8 text-gray-900 bg-gray-50 p-3 rounded-lg">
                            {Array.isArray(answer) ? answer.join(', ') : answer || 'No answer'}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-6 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-gray-400" />
                      <span className="text-gray-600">Submitted:</span>
                      <span className="font-semibold text-gray-900">{formatDate(selectedResponse.created_at)}</span>
                    </div>
                    {selectedResponse.ip_address && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">IP:</span>
                        <span className="font-mono text-gray-900">{selectedResponse.ip_address}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                  <BarChart3 size={64} className="text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Select a response</h3>
                  <p className="text-gray-600">Click on a response to view its details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

